import { AxiosError } from 'axios';
import { useContext, useContextSelector } from 'use-context-selector';
import { APIGatewayResponse } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import { AuthorizationPayrollContext } from './AuthorizationPayrollContext';

const useAuthorizationPayrollState = () => {
  const context = useContext(AuthorizationPayrollContext);

  if (context === undefined) {
    throw new Error('useAuthorizationPayrollState must be used within AuthorizationPayrollProvider');
  }

  return context;
};

const useAuthorizationPayrollDetails = () => {
  const context = useContextSelector(AuthorizationPayrollContext, state => state.payrollDetails || ({} as any));

  if (context === undefined) {
    throw new Error('useAuthorizationPayrollDetails must be used within AuthorizationPayrollProvider');
  }

  return context;
};

const useAuthorizationSelectedPayroll = () => {
  const context = useContextSelector(AuthorizationPayrollContext, state => state.selectedPayroll);

  if (context === undefined) {
    throw new Error('useAuthorizationSelectedPayroll must be used within AuthorizationPayrollProvider');
  }

  return context;
};

const useOnSelectAuthorizationPayroll = () => {
  const context = useContextSelector(AuthorizationPayrollContext, state => state.onSelectPayroll);

  if (context === undefined) {
    throw new Error('useOnSelectAuthorizationPayroll must be used within AuthorizationPayrollProvider');
  }

  return context;
};

const useOnResetSelectedAuthorizationPayroll = () => {
  const context = useContextSelector(AuthorizationPayrollContext, state => state.onResetSelectedPayroll);

  if (context === undefined) {
    throw new Error('useOnResetSelectedAuthorizationPayroll must be used within AuthorizationPayrollProvider');
  }

  return context;
};

const useAuthorizationPayrollLoadingState = (
  loadingProperty: 'isLoadingPayrollDetails' | 'isLoadingCheckAuthorization',
) => {
  const context = useContextSelector(AuthorizationPayrollContext, state => state.loadingState);

  if (context === undefined) {
    throw new Error('useAuthorizationPayrollLoadingState must be used within AuthorizationPayrollProvider');
  }

  return context[loadingProperty];
};

const useAuthorizationPayrollErrorState = (
  errorProperty: 'payrollDetailsError' | 'checkAuthorizationError',
): AxiosError<APIGatewayResponse<unknown>, any> | undefined => {
  const context = useContextSelector(AuthorizationPayrollContext, state => state.errorState);

  if (context === undefined) {
    throw new Error('useAuthorizationPayrollErrorState must be used within AuthorizationPayrollProvider');
  }

  return context[errorProperty];
};

const useOnTogglePayrollAuthFlowDrawer = () => {
  const context = useContextSelector(AuthorizationPayrollContext, state => state.onToggleAuthPayrollFlowDrawer);

  if (context === undefined) {
    throw new Error('useOnTogglePayrollAuthFlowDrawer must be used within AuthorizationPayrollProvider');
  }

  return context;
};

const useIsPayrollAuthFlowDrawerOpen = () => {
  const context = useContextSelector(AuthorizationPayrollContext, state => state.isAuthPayrollFlowDrawerOpen);

  if (context === undefined) {
    throw new Error('useIsPayrollAuthFlowDrawerOpen must be used within AuthorizationPayrollProvider');
  }

  return context;
};

const useCheckPayrollAuthorization = () =>
  useContextSelector(AuthorizationPayrollContext, state => state.checkAuthorizationData);

const useExecutePayrollMandateService = () => {
  const context = useContextSelector(AuthorizationPayrollContext, state => state.executeMandateService);

  if (context === undefined) {
    throw new Error('useExecutePayrollMandateService must be used within AuthorizationPayrollProvider');
  }

  return context;
};

const useInitiatePayrollMandateService = () => {
  const context = useContextSelector(AuthorizationPayrollContext, state => state.initiateMandateService);

  if (context === undefined) {
    throw new Error('useInitiatePayrollMandateService must be used within AuthorizationPayrollProvider');
  }

  return context;
};

const useOnRefetchPayrollDetailsMutation = () => {
  const context = useContextSelector(AuthorizationPayrollContext, state => state.onRefetchPayrollDetailsMutation);

  if (context === undefined) {
    throw new Error('useOnRefetchPayrollDetailsMutation must be used within AuthorizationPayrollProvider');
  }

  return context;
};

const useOnCheckPayrollAuthorizationMutation = () => {
  const context = useContextSelector(AuthorizationPayrollContext, state => state.onCheckAuthorizationMutation);

  if (context === undefined) {
    throw new Error('useOnCheckPayrollAuthorizationMutation must be used within AuthorizationPayrollProvider');
  }

  return context;
};

export {
  useAuthorizationPayrollState,
  useAuthorizationPayrollDetails,
  useAuthorizationSelectedPayroll,
  useOnSelectAuthorizationPayroll,
  useOnResetSelectedAuthorizationPayroll,
  useAuthorizationPayrollLoadingState,
  useAuthorizationPayrollErrorState,
  useOnTogglePayrollAuthFlowDrawer,
  useIsPayrollAuthFlowDrawerOpen,
  useCheckPayrollAuthorization,
  useExecutePayrollMandateService,
  useInitiatePayrollMandateService,
  useOnRefetchPayrollDetailsMutation,
  useOnCheckPayrollAuthorizationMutation,
};
